export type SpeechMode = "bouyomi" | "browser";

export interface SpeechSettings {
  alwaysEnabled: boolean;
  autoScroll: boolean;
  port: number;
  enabledThreadIds: string[];
  disabledThreadIds?: string[];
  mode: SpeechMode;
  rate: number;
  volume: number;
}

export const SPEECH_SETTINGS_EVENT = "aimg:speech-settings";
export const SPEECH_REQUEST_DEADLINE_MS = 5000;
const SPEECH_MS_PER_CHAR = 200;
const SPEECH_SAFETY_MARGIN_MS = 2000;

export type SpeechResult =
  | {
      status: "completed";
      transport: "browser";
      completion: "audio-ended";
    }
  | {
      status: "completed";
      transport: "bouyomi";
      completion: "request-finished";
    }
  | { status: "aborted"; reason: "cancelled" }
  | {
      status: "failed";
      transport: "browser" | "bouyomi";
      reason:
        | "request-timeout"
        | "request-error"
        | "audio-timeout"
        | "audio-error";
      cause?: unknown;
    }
  | {
      status: "skipped";
      reason: "empty" | "disabled" | "not-eligible" | "duplicate";
    }
  | {
      status: "unavailable";
      reason:
        | "lock-api"
        | "lock-timeout"
        | "storage"
        | "ledger"
        | "lock-request"
        | "missing-delivery-id"
        | "browser-api";
    };

export const DEFAULT_SPEECH_SETTINGS: SpeechSettings = {
  alwaysEnabled: false,
  autoScroll: false,
  port: 50080,
  enabledThreadIds: [],
  disabledThreadIds: [],
  mode: "bouyomi",
  rate: 1,
  volume: 1,
};

function validPort(value: unknown): number | null {
  return typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= 65535
    ? value
    : null;
}

function portFromEndpoint(value: unknown): number | null {
  if (typeof value !== "string" || value === "") return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "http:") return null;
    return validPort(url.port === "" ? 80 : Number(url.port));
  } catch {
    return null;
  }
}

function finiteNumber(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function loadSpeechSettings(
  storage: Pick<Storage, "getItem">,
): SpeechSettings {
  try {
    const raw = JSON.parse(storage.getItem("bouyomiSettings") ?? "null") as
      | (Partial<SpeechSettings> & { endpoint?: unknown })
      | null;
    if (!raw) return DEFAULT_SPEECH_SETTINGS;
    return {
      alwaysEnabled:
        typeof raw.alwaysEnabled === "boolean" ? raw.alwaysEnabled : false,
      autoScroll: typeof raw.autoScroll === "boolean" ? raw.autoScroll : false,
      port: validPort(raw.port) ?? portFromEndpoint(raw.endpoint) ?? 50080,
      enabledThreadIds: Array.isArray(raw.enabledThreadIds)
        ? [...new Set(raw.enabledThreadIds)].filter(
            (value): value is string => typeof value === "string",
          )
        : [],
      disabledThreadIds: Array.isArray(raw.disabledThreadIds)
        ? [...new Set(raw.disabledThreadIds)].filter(
            (value): value is string => typeof value === "string",
          )
        : [],
      mode: raw.mode === "browser" ? "browser" : "bouyomi",
      rate: Math.max(0.5, Math.min(2, finiteNumber(raw.rate, 1))),
      volume: Math.max(0, Math.min(1, finiteNumber(raw.volume, 1))),
    };
  } catch {
    return DEFAULT_SPEECH_SETTINGS;
  }
}

export function loadBrowserSpeechSettings(): SpeechSettings {
  try {
    return loadSpeechSettings(localStorage);
  } catch {
    return DEFAULT_SPEECH_SETTINGS;
  }
}

export function saveSpeechSettings(
  storage: Pick<Storage, "setItem">,
  settings: SpeechSettings,
): boolean {
  try {
    storage.setItem("bouyomiSettings", JSON.stringify(settings));
  } catch {
    return false;
  }

  try {
    if (typeof dispatchEvent === "function" && typeof Event !== "undefined") {
      dispatchEvent(new Event(SPEECH_SETTINGS_EVENT));
    }
  } catch {
    // A failed notification does not undo the successful storage write.
  }
  return true;
}

export function saveBrowserSpeechSettings(settings: SpeechSettings): boolean {
  try {
    return saveSpeechSettings(localStorage, settings);
  } catch {
    return false;
  }
}

export function speechText(body: string): string {
  const normalized = body
    .replace(/https?:\/\/\S+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
  return normalized.length > 200
    ? `${normalized.slice(0, 200)}...`
    : normalized;
}

export function isSpeechEnabled(
  settings: SpeechSettings,
  threadId: string,
): boolean {
  if (settings.disabledThreadIds?.includes(threadId)) return false;
  return settings.alwaysEnabled || settings.enabledThreadIds.includes(threadId);
}

export function isBrowserSpeechAvailable(): boolean {
  if (
    typeof SpeechSynthesisUtterance === "undefined" ||
    !("speechSynthesis" in globalThis)
  ) {
    return false;
  }
  try {
    const synthesis = globalThis.speechSynthesis;
    return (
      typeof synthesis?.speak === "function" &&
      typeof synthesis.cancel === "function"
    );
  } catch {
    return false;
  }
}

async function speakWithBrowser(
  body: string,
  settings: SpeechSettings,
  signal?: AbortSignal,
): Promise<SpeechResult> {
  if (!isBrowserSpeechAvailable()) {
    return { status: "unavailable", reason: "browser-api" };
  }

  if (signal?.aborted) return { status: "aborted", reason: "cancelled" };

  return new Promise<SpeechResult>((resolve) => {
    const utterance = new SpeechSynthesisUtterance(body);
    const synthesis = globalThis.speechSynthesis;
    let settled = false;
    let safetyTimer: ReturnType<typeof setTimeout> | null = null;

    const finish = (result: SpeechResult, cancel = false): void => {
      if (settled) return;
      settled = true;
      if (safetyTimer !== null) clearTimeout(safetyTimer);
      utterance.onend = null;
      utterance.onerror = null;
      signal?.removeEventListener("abort", handleAbort);
      if (cancel) {
        try {
          synthesis.cancel();
        } catch {
          // Some implementations can throw while the document is being torn down.
        }
      }
      resolve(result);
    };
    const handleAbort = (): void =>
      finish({ status: "aborted", reason: "cancelled" }, true);

    utterance.lang = "ja-JP";
    utterance.rate = settings.rate;
    utterance.volume = settings.volume;
    utterance.onend = (): void =>
      finish({
        status: "completed",
        transport: "browser",
        completion: "audio-ended",
      });
    utterance.onerror = (event: SpeechSynthesisErrorEvent): void =>
      finish({
        status: "failed",
        transport: "browser",
        reason: "audio-error",
        cause: event,
      });

    safetyTimer = setTimeout(
      () => {
        finish(
          {
            status: "failed",
            transport: "browser",
            reason: "audio-timeout",
          },
          true,
        );
      },
      (body.length * SPEECH_MS_PER_CHAR) / settings.rate +
        SPEECH_SAFETY_MARGIN_MS,
    );
    signal?.addEventListener("abort", handleAbort, { once: true });
    if (signal?.aborted) {
      handleAbort();
      return;
    }

    try {
      synthesis.speak(utterance);
    } catch (cause: unknown) {
      finish({
        status: "failed",
        transport: "browser",
        reason: "audio-error",
        cause,
      });
    }
  });
}

export async function speakText(
  text: string,
  settings: SpeechSettings,
  fetcher: typeof fetch = fetch,
  signal?: AbortSignal,
): Promise<SpeechResult> {
  const body = speechText(text);
  if (!body) return { status: "skipped", reason: "empty" };
  if (signal?.aborted) return { status: "aborted", reason: "cancelled" };
  if (settings.mode === "browser") {
    return speakWithBrowser(body, settings, signal);
  }

  const url = `http://localhost:${settings.port}/Talk?text=${encodeURIComponent(body)}`;
  const requestController = new AbortController();
  let timedOut = false;
  let settled = false;

  return new Promise<SpeechResult>((resolve) => {
    const finish = (result: SpeechResult): void => {
      if (settled) return;
      settled = true;
      clearTimeout(deadlineTimer);
      signal?.removeEventListener("abort", handleAbort);
      resolve(result);
    };
    const handleAbort = (): void => {
      requestController.abort();
      finish({ status: "aborted", reason: "cancelled" });
    };
    const deadlineTimer = setTimeout(() => {
      timedOut = true;
      requestController.abort();
      finish({
        status: "failed",
        transport: "bouyomi",
        reason: "request-timeout",
      });
    }, SPEECH_REQUEST_DEADLINE_MS);

    signal?.addEventListener("abort", handleAbort, { once: true });
    if (signal?.aborted) {
      handleAbort();
      return;
    }

    // no-corsではHTTP応答を検証できない。reject時に棒読みちゃんが受信済みの
    // 可能性もあるので、この関数とqueueは自動再送しない。
    void Promise.resolve()
      .then(() =>
        fetcher(url, { mode: "no-cors", signal: requestController.signal }),
      )
      .then(
        () => {
          finish({
            status: "completed",
            transport: "bouyomi",
            completion: "request-finished",
          });
        },
        (cause: unknown) => {
          if (signal?.aborted) {
            finish({ status: "aborted", reason: "cancelled" });
            return;
          }
          if (timedOut) {
            finish({
              status: "failed",
              transport: "bouyomi",
              reason: "request-timeout",
              cause,
            });
            return;
          }
          finish({
            status: "failed",
            transport: "bouyomi",
            reason: "request-error",
            cause,
          });
        },
      );
  });
}
